import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CalendarDays, Clock, Users, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
  "2:00 PM", "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM",
  "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM"
];

const guestOptions = ["1", "2", "3", "4", "5", "6", "7", "8+"];

const ReservationsPage = () => {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    specialRequests: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !time || !guests || !formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: date.toISOString().split('T')[0],
        time: time,
        guests: parseInt(guests.replace('+', '')),
        special_requests: formData.specialRequests || null
      });
      
      setSuccess(true);
      toast.success("Reservation confirmed! We'll see you soon.");
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="reservation-success" className="min-h-screen bg-[#0C0C0E] pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-6"
        >
          <div className="w-20 h-20 rounded-full bg-saffron/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-saffron" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">
            Reservation <span className="italic text-saffron">Confirmed</span>
          </h1>
          <p className="font-body text-white/60 text-lg mb-8 max-w-md mx-auto">
            Thank you, {formData.name}! We've sent a confirmation to {formData.email}.
          </p>
          <div className="bg-surface/50 border border-white/5 rounded-xl p-6 inline-block text-left">
            <div className="flex items-center gap-4 mb-4">
              <CalendarDays className="w-5 h-5 text-saffron" />
              <span className="font-body text-white">{date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <Clock className="w-5 h-5 text-saffron" />
              <span className="font-body text-white">{time}</span>
            </div>
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-saffron" />
              <span className="font-body text-white">{guests} {parseInt(guests) === 1 ? 'Guest' : 'Guests'}</span>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="reservations-page" className="min-h-screen bg-[#0C0C0E] pt-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column - Image & Info */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-32"
            >
              <p className="font-accent text-saffron text-xl mb-3">Reserve Your Table</p>
              <h1 className="font-heading text-5xl md:text-6xl text-white mb-6 leading-tight">
                Join Us for an <span className="italic text-saffron">Unforgettable</span> Experience
              </h1>
              <p className="font-body text-white/50 text-lg mb-8">
                Book your table and immerse yourself in the flavors of Persia. 
                For parties larger than 8, please contact us directly.
              </p>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80"
                  alt="Restaurant ambiance"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
            </motion.div>
          </div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Date Selection */}
              <div>
                <label className="flex items-center gap-2 font-heading text-white text-lg mb-4">
                  <CalendarDays className="w-5 h-5 text-saffron" />
                  Select Date
                </label>
                <div className="bg-surface/50 border border-white/5 rounded-xl p-4 inline-block">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date() || date < new Date("1900-01-01")}
                    data-testid="reservation-calendar"
                    className="text-white"
                  />
                </div>
              </div>

              {/* Time & Guests Row */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 font-heading text-white text-lg mb-4">
                    <Clock className="w-5 h-5 text-saffron" />
                    Select Time
                  </label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="time-select" className="bg-surface/50 border-white/10 text-white h-14">
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot} className="text-white hover:bg-saffron/20">
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="flex items-center gap-2 font-heading text-white text-lg mb-4">
                    <Users className="w-5 h-5 text-saffron" />
                    Number of Guests
                  </label>
                  <Select value={guests} onValueChange={setGuests}>
                    <SelectTrigger data-testid="guests-select" className="bg-surface/50 border-white/10 text-white h-14">
                      <SelectValue placeholder="Select guests" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10">
                      {guestOptions.map((num) => (
                        <SelectItem key={num} value={num} className="text-white hover:bg-saffron/20">
                          {num} {num === "1" ? "Guest" : "Guests"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-6">
                <h3 className="font-heading text-white text-xl">Your Details</h3>
                
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="text"
                      placeholder="Full Name *"
                      data-testid="reservation-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="Phone Number *"
                      data-testid="reservation-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <input
                    type="email"
                    placeholder="Email Address *"
                    data-testid="reservation-email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <textarea
                    placeholder="Special Requests (optional)"
                    data-testid="reservation-requests"
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                    rows={3}
                    className="w-full bg-transparent border-b border-white/20 focus:border-saffron px-0 py-4 text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                data-testid="submit-reservation-btn"
                disabled={loading}
                className="w-full py-5 rounded-full bg-saffron text-white font-heading italic text-lg hover:bg-saffron/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Confirming..." : "Confirm Reservation"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReservationsPage;
